import { useCallback, useEffect, useState } from 'react'

import { useDeletePOPDFLabelSetPresenter } from '@webapp/modules/purchasing/features/delete-po-pdf-label-set/presenters/UseDeletePOPDFLabelSetPresenter'
import { useGetInitialPOPDFLabelSetPresenter } from '@webapp/modules/purchasing/features/get-initial-po-pdf-label-set/presenters/UseGetInitialPOPDFLabelSetPresenter'
import { useSavePOPDFLabelOverridesPresenter } from '@webapp/modules/purchasing/features/save-po-pdf-label-overrides/presenters/UseSavePOPDFLabelOverridesPresenter'
import { useSetDefaultPOPDFLabelSetPresenter } from '@webapp/modules/purchasing/features/set-default-po-pdf-label-set/presenters/UseSetDefaultPOPDFLabelSetPresenter'
import {
  type PDFLabelFieldMetadata,
  type PDFLabelSet,
} from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules

export type POPDFLabelsModalState = 'create' | 'rename' | null

/**
 * Orchestration hook for POPDFLabelsView.
 *
 * Owns the active-set state read/written by seven features plus the
 * existing PDFLabelSetSelect: seeding from the initial-set query (BHV-032),
 * switching (BHV-036), post-create/rename activation (BHV-037/038),
 * post-delete reselection (BHV-041), current-primary tracking (BHV-039)
 * and the create/rename modal lifecycle.
 */
export const usePOPDFLabelsView = () => {
  const { initialSet, isLoading } = useGetInitialPOPDFLabelSetPresenter()

  // The initial-set resolution seeds the selection until the user makes an
  // explicit choice; delete's reselection hands control back to it (the
  // delete command invalidates initialSet(), whose refetch resolves the
  // first remaining set or null → empty state)
  const [selectedSet, setSelectedSet] = useState<PDFLabelSet | null>(null)
  const [hasUserSelection, setHasUserSelection] = useState(false)
  const activeSet = hasUserSelection ? selectedSet : initialSet

  const [currentPrimaryKey, setCurrentPrimaryKey] = useState<string | null>(
    null
  )
  // Seeded from the initial resolution and re-synced by every initialSet()
  // invalidation the command Models perform
  useEffect(() => {
    setCurrentPrimaryKey(initialSet?.isPrimary ? initialSet.key : null)
  }, [initialSet])

  const [modalState, setModalState] = useState<POPDFLabelsModalState>(null)

  const selectSet = useCallback((set: PDFLabelSet) => {
    setSelectedSet(set)
    setHasUserSelection(true)
  }, [])

  const {
    commands: { saveOverride },
    states: { isSaving: isSavingOverride },
  } = useSavePOPDFLabelOverridesPresenter()

  const {
    commands: { toggleDefault },
    states: { isSaving: isSavingDefault },
  } = useSetDefaultPOPDFLabelSetPresenter({
    onSuccess: setCurrentPrimaryKey,
  })

  const {
    commands: { deleteSet },
    states: { isDeleting },
  } = useDeletePOPDFLabelSetPresenter({
    onDeleted: () => {
      setSelectedSet(null)
      setHasUserSelection(false)
    },
  })

  const openCreateModal = useCallback(() => setModalState('create'), [])
  const openRenameModal = useCallback(() => setModalState('rename'), [])
  const closeModal = useCallback(() => setModalState(null), [])

  const handleSelectChange = useCallback(
    (set: PDFLabelSet | null) => {
      if (set) selectSet(set)
    },
    [selectSet]
  )

  // Post-success activation: the created/renamed set becomes active
  const handleCreateSuccess = useCallback(
    (set: PDFLabelSet) => {
      selectSet(set)
      setModalState(null)
    },
    [selectSet]
  )

  const handleRenameSuccess = useCallback(
    (set: PDFLabelSet) => {
      selectSet(set)
      setModalState(null)
    },
    [selectSet]
  )

  const onOverrideCommit = useCallback(
    (
      translationType: PDFLabelFieldMetadata['translationType'],
      fieldKey: string,
      value: string
    ) => {
      if (activeSet) {
        saveOverride(activeSet, translationType, fieldKey, value)
      }
    },
    [activeSet, saveOverride]
  )

  const onDeleteSet = useCallback(() => {
    if (activeSet) deleteSet(activeSet)
  }, [activeSet, deleteSet])

  const onToggleDefault = useCallback(() => {
    if (activeSet) toggleDefault(activeSet, currentPrimaryKey)
  }, [activeSet, currentPrimaryKey, toggleDefault])

  return {
    data: {
      activeSet,
      currentPrimaryKey,
    },
    states: {
      isLoading,
      modalState,
      isSavingOverride,
      isSavingDefault,
      isDeleting,
    },
    methods: {
      openCreateModal,
      openRenameModal,
      closeModal,
      handleSelectChange,
      handleCreateSuccess,
      handleRenameSuccess,
      onOverrideCommit,
      onDeleteSet,
      onToggleDefault,
    },
  }
}
